
import { Component, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import Notification from '@/mixins/Notification';
import StackedForm from '@/mixins/StackedForm';
import { Venue } from '@/interfaces/models/Venue';
import { namespace } from 'vuex-class';
import { Upsell, GoalUpsell as GoalUpsellModel } from '@/interfaces/models/Upsell';
import { UpsellType } from '@/enums/UpsellType';
import { isAxiosError } from '@/util/helper';
import VTable from '@/components/shared/table/VTable.vue';
import { GOAL_HEADERS } from '@/components/upsell/headers';
import GoalForm from '@/components/upsell/GoalForm.vue';
import FoodcardApiService from '@/api/http/FoodcardApiService';
import { AxiosResponse } from 'axios';
import { Article } from '@/interfaces/models/Article';

const app = namespace('app');
const venue = namespace('venue');
const upsell = namespace('upsell');

@Component({
  components: { GoalForm, VTable },
})
export default class GoalUpsell extends mixins(Notification, StackedForm) {
  public headers = GOAL_HEADERS;

  @venue.State('active') public venue!: Venue;
  @app.State('venues') public venues!: Venue[];

  @upsell.State('goal') public upsell!: Upsell<GoalUpsellModel>;
  @upsell.Action('showByVenueAndType') public showByVenueAndType!: any;
  @upsell.Action('store') public store!: any;
  @upsell.Action('update') public update!: any;
  @upsell.Action('destroy') public destroy!: any;

  public editModalVisible: boolean = false;
  public articles: Partial<Article[]> = [];
  public activeUpsellItem: GoalUpsellModel | null = null;
  public isMaster: boolean = false;

  public $refs!: {
    form: InstanceType<typeof GoalForm> & {
      getData: () => any;
      validate: () => Promise<boolean | boolean[]>;
    };
  };

  public async mounted() {
    if (!this.venue) return;
    this.reload();
  }

  protected async reload() {
    await this.loadArticles();
    try {
      await this.showByVenueAndType({ type: UpsellType.GOAL, venue: this.venue._id });
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        return;
      }
      throw err;
    } finally {
      this.isMaster = this.upsell?.isMaster || false;
    }
  }

  protected normalizeUpsells(upsell: GoalUpsellModel) {
    return {
      ...upsell,
      // @ts-ignore
      mov: upsell.mov['$numberDecimal'] ? upsell.mov['$numberDecimal'] : upsell.mov,
      // @ts-ignore
      roundTo: upsell.roundTo['$numberDecimal'] ? upsell.roundTo['$numberDecimal'] : upsell.roundTo,
    };
  }

  protected async loadArticles() {
    if (!this.venue) {
      return;
    }
    const api: FoodcardApiService = new FoodcardApiService();
    await api.getArticleNames({ venue: this.venue._id }).then((res: AxiosResponse<Partial<Article>[]>) => {
      this.articles = res.data as any;
    });
  }

  protected async save() {
    this.$refs.form.validate().then(async (result: boolean | boolean[]) => {
      if (!this.isValid(result)) {
        this.notifyError('notification.form');
        return;
      }

      const data: GoalUpsellModel = this.$refs.form.getData();

      this.$startLoading('upsell.save');

      if (this.activeUpsellItem) {
        const index = this.upsell.upsells.findIndex((upsell: GoalUpsellModel) => upsell === this.activeUpsellItem);
        this.upsell.upsells[index] = { ...this.activeUpsellItem, ...data };
        await this.update({
          id: this.upsell._id,
          ...this.upsell,
          isMaster: this.isMaster,
          upsells: [...this.upsell.upsells.map(this.normalizeUpsells)],
        });
      } else if (this.upsell) {
        await this.update({
          id: this.upsell._id,
          ...this.upsell,
          isMaster: this.isMaster,
          upsells: [...this.upsell.upsells.map(this.normalizeUpsells), data],
        });
      } else {
        await this.store({
          type: UpsellType.GOAL,
          venue: this.venue._id,
          isMaster: this.isMaster,
          upsells: [{ ...data }],
        });
      }
      this.$stopLoading('upsell.save');
      this.reset();
    });
  }

  protected reset() {
    this.editModalVisible = false;
    this.activeUpsellItem = null;
  }

  protected async deleteUpsell(item: GoalUpsellModel) {
    this.$startLoading('upsell.save');

    if (this.upsell.upsells.length === 1) {
      await this.destroy({ id: this.upsell._id, type: UpsellType.GOAL });
      this.$stopLoading('upsell.save');
      this.isMaster = false;
      return;
    }

    const newUpsells = this.upsell.upsells.filter((upsell: GoalUpsellModel) => upsell !== item);
    await this.update({
      id: this.upsell._id,
      ...this.upsell,
      upsells: newUpsells.map(this.normalizeUpsells),
    });
    this.$stopLoading('upsell.save');
  }

  protected startEditingUpsell(item: GoalUpsellModel) {
    this.activeUpsellItem = item;
    this.editModalVisible = true;
  }

  protected getFullArticleById(id: string) {
    // @ts-ignore
    return this.articles.find((art: Article) => (this.isMaster ? (art.masterId || art._id) === id : art._id === id));
  }

  protected prettifyArticles(articles: string[]) {
    const pretty = [];
    for (const article of articles) {
      // @ts-ignore
      const fullArticle = this.getFullArticleById(article);
      if (fullArticle) {
        pretty.push(`${this.$options.filters!.localized(fullArticle.name)} (${fullArticle.number})`);
      } else {
        pretty.push(article);
      }
    }
    return pretty.join(', ');
  }

  @Watch('venue')
  protected async onVenueChange() {
    if (this.venue?._id && !this.$isLoading('upsell')) {
      this.$startLoading('upsell');
      await this.reload();
      this.$stopLoading('upsell');
    }
  }
}
